/* eslint-disable max-len */
import { Paper } from '@mui/material';
import {
  FORWARDING_RULE_ALGORITHM, HTTP_METHOD, ROUTE, VALIDATION,
} from 'constant';
import React from 'react';
import { useSelector } from 'react-redux';
import { actions } from 'redux/actions';
import { ACTIONS_TYPES } from 'redux/helper';
import Shared from 'screens/shared';
import { copyObject, enumToArray, isEmptyObject } from 'utils';

export default ({ history, match }) => {
  let { id } = match.params;
  id = id !== 'create' ? id : null;
  const { [ACTIONS_TYPES.TARGET_GROUP.GET]: targetGroup = {} } = useSelector((e) => e);

  let model = {
    protocol: 'HTTP',
    healthCheck: {
      checkTimeout: 50000,
      checkInterval: 2000,
      retries: 3,
    },
  };

  if (id) {
    if (!isEmptyObject(targetGroup)) {
      model = targetGroup.properties;
      model.httpHealthCheck = !isEmptyObject(model.httpHealthCheck) ? { ...model.httpHealthCheck, isHTTPHealthCheck: true } : {};
      model.healthCheck = !isEmptyObject(model.healthCheck) ? { ...model.healthCheck, isHealthCheck: true } : {
        checkTimeout: 50000, checkInterval: 2000, retries: 3,
      };
    } else {
      history.push(ROUTE.DASHBOARD.TARGET_GROUP.path.replace(':id', id));
    }
  } else {
    model.httpHealthCheck = { isHTTPHealthCheck: false };
  }

  const saveHandler = (e) => {
    const { healthCheck = {}, httpHealthCheck = {}, ...values } = copyObject(e);
    const { isHealthCheck, ...healthCheckProps } = healthCheck;
    if (isHealthCheck) {
      values.healthCheck = healthCheckProps;
    }

    const { isHTTPHealthCheck, ...httpHealthCheckProps } = httpHealthCheck;

    if (isHTTPHealthCheck) {
      values.httpHealthCheck = httpHealthCheckProps;
    }

    if (id) {
      actions.targetGroup.update({ id, properties: values });
      history.push(ROUTE.DASHBOARD.TARGET_GROUP.path.replace(':id', id));
    } else {
      actions.targetGroup.create({ properties: values });
      history.push(ROUTE.DASHBOARD.TARGET_GROUPS.path);
    }
  };

  const fields = [
    {
      name: 'name',
      type: 'text',
      title: 'Name',
    },
    {
      name: 'algorithm',
      type: 'dropdown',
      title: 'Algorithm',
      options: enumToArray(FORWARDING_RULE_ALGORITHM).map((e) => [e.name, e.label]),
      helpText: 'Algorithm for the balancing.',
    },
    {
      name: 'protocol',
      type: 'dropdown',
      title: 'Protocol',
      options: ['HTTP'],
      helpText: 'Protocol of the balancing.',
    },
    {
      name: 'healthCheck',
      title: 'Connection Settings',
      type: 'object',
      fields: [
        {
          name: 'isHealthCheck',
          type: 'switch',
          title: 'Change Default Connection Settings',
          helpText: 'The default values for the connection settings has been selected. If you want to change, please enable this switch.',
        },
        {
          name: 'checkTimeout',
          type: 'text',
          title: 'Client Timeout (Milliseconds)',
          helpText: 'checkTimeout is expressed in milliseconds. This inactivity timeout applies when the client is expected to acknowledge or send data. If unset the default of 50 seconds will be used.',
          setVisibility: (e) => e.isHealthCheck,
        },
        {
          name: 'retries',
          type: 'text',
          title: 'Retries',
          helpText: 'Retries specifies the number of retries to perform on a target VM after a connection failure. If unset, the default value of 3 will be used. (valid range: [0, 65535])',
          setVisibility: (e) => e.isHealthCheck,
        },
        {
          name: 'checkInterval',
          type: 'text',
          title: 'Check Interval (MS)',
          helpText: 'CheckInterval determines the duration (in milliseconds) between consecutive health checks. If unspecified a default of 2000 ms is used.',
          setVisibility: (e) => e.isHealthCheck,
        },
      ],
      validations: [
        {
          name: 'checkTimeout',
          type: VALIDATION.NUMBER,
          min: 0,
          max: 86400000, // one day
        },
        {
          name: 'connectTimeout',
          type: VALIDATION.NUMBER,
          min: 0,
          max: 86400000, // one day
        },
        {
          name: 'targetTimeout',
          type: VALIDATION.NUMBER,
          min: 0,
          max: 86400000, // one day
        },
        {
          name: 'retries',
          type: VALIDATION.NUMBER,
          min: 0,
          max: 65535,
        },
      ],
    },
    {
      name: 'httpHealthCheck',
      title: 'Health Check Settings',
      type: 'object',
      fields: [
        {
          name: 'isHTTPHealthCheck',
          type: 'switch',
          title: 'Is Health Check Enable?',
        },
        {
          name: 'path',
          type: 'text',
          title: 'HTTP Path',
          helpText: 'The path for the Health Check',
          setVisibility: (e) => e.isHTTPHealthCheck,
        },
        {
          name: 'method',
          type: 'dropdown',
          title: 'HTTP Method',
          options: enumToArray(HTTP_METHOD),
          helpText: 'The method for the Health Check.',
          setVisibility: (e) => e.isHTTPHealthCheck,
        },
        {
          name: 'matchType',
          type: 'dropdown',
          title: 'Response matching type',
          options: [['STATUS_CODE', 'Status Code'], ['RESPONSE_BODY', 'Response Body']],
          setVisibility: (e) => e.isHTTPHealthCheck,
        },
        {
          name: 'regex',
          type: 'switch',
          title: 'Is response matching through Regex?',
          setVisibility: (e) => e.isHTTPHealthCheck,
        },
        {
          name: 'response',
          type: 'textarea',
          title: 'Response to match',
          // eslint-disable-next-line react/jsx-one-expression-per-line
          helpText: <>The response returned by the request to match based on the selection of <b>Response matching type</b> field.</>,
          setVisibility: (e) => e.isHTTPHealthCheck,
        },
        {
          name: 'negate',
          type: 'switch',
          title: 'Negate the response matching',
          helpText: 'If enable then the condition of response matching will be negated.',
          setVisibility: (e) => e.isHTTPHealthCheck,
        },
      ],
      validations: [
        {
          name: 'path',
          type: VALIDATION.STRING,
          max: 1024,
        },
        {
          name: 'matchType',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'response',
          type: VALIDATION.REQUIRED,
          max: 1024 * 1024,
        },
      ],
    },
    {
      name: 'targets',
      type: 'array',
      title: 'Targets',
      isAddButton: true,
      actionButtonName: 'Add Target',
      icon: 'bullseye',
      setItemTitle: (e) => e.ip,
      setItemDetails: (e) => `Port: ${e.port}, Health Check: ${e.healthCheckEnabled ? 'Enabled' : 'Disabled'}`,
      initValues: {
        ip: '',
        port: '',
        weight: 1,
        healthCheckEnabled: false,
        maintenanceEnabled: false,
      },
      fields: [
        {
          name: 'ip',
          type: 'text',
          title: 'IP',
        },
        {
          name: 'port',
          title: 'Port',
          type: 'port',
        },
        {
          name: 'weight',
          type: 'text',
          title: 'Weight',
        },
        {
          name: 'healthCheckEnabled',
          type: 'switch',
          title: 'Is Health Check enable?',
          helpText: 'Check specifies whether the target VM\'s health is checked. If turned off, a target VM is always considered available. If turned on, the target VM is available when accepting periodic TCP connections, to ensure that it is really able to serve requests. The address and port to send the tests to are those of the target VM. The health check only consists of a connection attempt.',
        },
        {
          name: 'maintenanceEnabled',
          type: 'switch',
          title: 'Activate the Maintenance mode',
          helpText: 'Maintenance specifies if a target VM should be marked as down, even if it is not.',
        },
      ],
      validations: [
        {
          name: 'ip',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'ip',
          type: VALIDATION.IP_V4,
        },
        {
          name: 'port',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'port',
          type: VALIDATION.NUMBER,
          min: 1,
          max: 65535,
        },
        {
          name: 'weight',
          type: VALIDATION.REQUIRED,
        },
        {
          name: 'weight',
          type: VALIDATION.NUMBER,
          min: 1,
          max: 256,
        },
      ],
    },
  ];
  const validations = [
    {
      name: 'name',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'name',
      type: VALIDATION.STRING,
      max: 256,
    },
    {
      name: 'algorithm',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'protocol',
      type: VALIDATION.REQUIRED,
    },
  ];

  return (
    <Shared.widgets.Page route={ROUTE.DASHBOARD.TARGET_GROUPS} title="Create Target Group" isStrap={false}>
      <Paper style={{ marginTop: 10 }}>
        <Shared.forms.Form
          fields={fields}
          fieldsValues={model}
          onSubmit={saveHandler}
          isCancel={false}
          validations={validations}
          changeFields={[
            { name: 'templateId', props: {} },
          ]}
          submitTitle={id ? 'Update Target Group' : 'Create Target Group'}
        />
      </Paper>
    </Shared.widgets.Page>
  );
};
